import {BookOpen, Apple, Heart, Dumbbell} from 'lucide-react'

const benefits = [
  {
    icon: Heart,
    title: "Heart Health",
    desc: "Healthy fats like omega-3s reduce inflammation and support cardiovascular function.",
  },
  {
    icon: Apple,
    title: "Nutrient Absorption",
    desc: "Vitamins A, D, E, and K are fat-soluble — you need dietary fat to absorb them.",
  },
  {
    icon: Dumbbell,
    title: "Muscle & Performance",
    desc: "Fat provides sustained energy for workouts and supports hormone production including testosterone.",
  },
]

export default function EducationSection() {
  return (
    <section id="education" className="relative py-16 sm:py-20 bg-gray-50 noise-bg">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 mb-4">
            <BookOpen className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Learn</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
            How to Calculate Fat Intake for Your Body Goals
          </h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            Understanding how to <strong>calculate fat grams per day</strong> is essential for anyone serious about nutrition.
            Whether you are using a <strong>fat requirement calculator</strong> for weight loss, bodybuilding, or general health,
            knowing your optimal fat intake ensures your body gets the fuel it needs.
          </p>
        </div>

        {/* Method Explanation */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 mb-12">
          <h3 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            The Mifflin-St Jeor Method
          </h3>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                title: "Calculate BMR",
                desc: "Your Basal Metabolic Rate is the calories your body burns at rest. We use the Mifflin-St Jeor equation — the gold standard for accuracy.",
              },
              {
                step: "02",
                title: "Determine TDEE",
                desc: "Total Daily Energy Expenditure factors in your activity level, giving a realistic picture of your daily calorie needs.",
              },
              {
                step: "03",
                title: "Calculate Fat Range",
                desc: "Health guidelines recommend 20-35% of calories from fat. Our daily fat intake calculator converts this to grams for easy tracking.",
              },
            ].map((item) => (
              <div key={item.step} className="relative">
                <span className="text-5xl font-extrabold text-emerald-100 absolute -top-2 -left-1">{item.step}</span>
                <div className="relative pt-8">
                  <h4 className="text-base font-bold text-slate-800 mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid sm:grid-cols-3 gap-6">
          {benefits.map((b) => {
            const Icon = b.icon
            return (
              <div
                key={b.title}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                  <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <h4 className="text-base font-bold text-slate-800 mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>{b.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            )
          })}
        </div>

        {/* SEO Content Paragraph */}
        <div className="mt-12 max-w-3xl">
          <p className="text-sm text-slate-500 leading-relaxed">
            A <strong>fat intake calculator for bodybuilding</strong> uses the same scientific foundation but may recommend
            staying closer to 25-30% of calories from fat to support muscle recovery and hormone optimization.
            Whether you need a <strong>fat needs calculator</strong> for cutting, bulking, or maintaining, the key is
            consistency. Track your macros, adjust based on results, and always prioritize whole-food fat sources like
            avocados, nuts, olive oil, and fatty fish.
          </p>
        </div>
      </div>
    </section>
  )
}
